import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Mail, Tag, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import UsersTable from '../../components/users/UsersTable';
import UserDetailDrawer from '../../components/users/UserDetailDrawer';
import { cn } from '../../lib/utils';

const UserListView = ({ 
  title, 
  description,
  users = [],
  onFilter,
  onSearch,
  onSort,
  onSelectUsers,
  onSendEmail,
  onAddTags,
  onExport
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('valueScore');
  const [sortDirection, setSortDirection] = useState('desc');

  // Calculate pagination
  const totalUsers = users.length;
  const totalPages = Math.ceil(totalUsers / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedUsers = users.slice(startIndex, endIndex);

  const handleSearch = (value) => {
    setSearchQuery(value);
    onSearch?.(value);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
    onSort?.(column, sortDirection);
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
    onSelectUsers?.(selectedUsers);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(paginatedUsers.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
    onSelectUsers?.(selectedUsers);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 p-6 space-y-6 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground/90">{title}</h1>
            {description && (
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selectedUsers.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 bg-white/5 hover:bg-white/10 border-white/10"
                  onClick={onSendEmail}
                >
                  <Mail className="h-4 w-4" />
                  Email ({selectedUsers.length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 bg-white/5 hover:bg-white/10 border-white/10"
                  onClick={onAddTags}
                >
                  <Tag className="h-4 w-4" />
                  Add Tags ({selectedUsers.length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 bg-white/5 hover:bg-white/10 border-white/10"
                  onClick={() => onExport?.(selectedUsers)}
                >
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters and Search */}
        <Card className="p-4 bg-card border-border hover:bg-card/90 transition-all duration-300">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground/70" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-8 bg-background border-border hover:border-border/40 focus:border-border/60"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 bg-background hover:bg-muted border-border">
                  <Filter className="h-4 w-4" />
                  Filter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[200px]">
                <DropdownMenuItem onClick={() => onFilter?.('all')}>
                  All Users
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onFilter?.('active')}>
                  Active Users
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onFilter?.('paid')}>
                  Paid Users
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onFilter?.('free')}>
                  Free Users
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </Card>

        {/* Users Table */}
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <UsersTable
            users={paginatedUsers}
            onSort={handleSort}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onRowClick={(user) => setSelectedUser(user)}
            selectedUsers={selectedUsers}
            onSelectUser={handleSelectUser}
            onSelectAll={handleSelectAll}
            viewMoreRoute={`/users/${title.toLowerCase().replace(/\s+/g, '-')}`}
          />
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1}-{Math.min(endIndex, totalUsers)} of {totalUsers} users
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="bg-background hover:bg-muted border-border"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="bg-background hover:bg-muted border-border"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* User Detail Drawer */}
      <UserDetailDrawer
        user={selectedUser}
        isOpen={!!selectedUser}
        onClose={() => setSelectedUser(null)}
      />
    </div>
  );
};

export default UserListView; 